import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

interface SuggestionItemV2 {
  title: string;
  description: string;
  key: number | string;
  group?: string;
  groupIcon?: string;
  highlighted?: boolean;
  wrap?: boolean;
  emitEvent?: boolean;
}

export class AutocompleteV2 implements Plugin {
  private readonly inputElement: HTMLInputElement | null;
  private readonly listContainer: HTMLElement | null;
  private readonly suggestionContainer: HTMLElement | null;
  private readonly clearIcon: HTMLElement | null = null;
  private readonly itemTemplates = new Map<string, HTMLElement>();
  private readonly headingTemplates = new Map<string, HTMLElement>();

  private items: SuggestionItemV2[] = [];
  private readonly minChars: number;
  private readonly searchTime: number;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private isDisabled: boolean = false;
  private oldSelection: SuggestionItemV2 | null = null;
  private selectionItem: SuggestionItemV2 | null = null;

  private readonly hiddenClass: string;
  private readonly visibleClass: string;

  constructor(private readonly element: HTMLElement) {
    const inputSelector = element.dataset.autocompleteInputSelector || "input";
    const listSelector =
      element.dataset.autocompleteListSelector || ".autocomplete-list";

    this.hiddenClass = element.dataset.autocompleteHiddenClass || "hidden";
    this.visibleClass = element.dataset.autocompleteVisibleClass || "visible";

    this.minChars = parseInt(element.dataset.autocompleteMinChars || "3", 10);
    this.searchTime = parseInt(
      element.dataset.autocompleteSearchTime || "0",
      10,
    );

    this.inputElement = element.querySelector<HTMLInputElement>(inputSelector);
    this.listContainer = element.querySelector<HTMLElement>(listSelector);
    this.suggestionContainer = element.querySelector<HTMLElement>(
      ".search-container__suggestions",
    );
    this.clearIcon = element.querySelector(".dsc-co-icon-close");

    this.parseItems();

    if (element.hasAttribute("data-autocomplete-disabled")) {
      this.isDisabled = true;
    }
  }

  init(): void {
    if (!this.inputElement || !this.listContainer) {
      return;
    }
    this.cacheTemplates();
    this.toggleClearIcon();
    this.setupUserInteractions();
    this.setupExternalControl();
    this.setupAttributeObserver();
  }

  private cacheTemplates(): void {
    this.element
      .querySelectorAll<HTMLElement>("[data-autocomplete-v2-key]")
      .forEach((item) => {
        const key = item.dataset.autocompleteV2Key;
        if (key) {
          this.itemTemplates.set(key, item.cloneNode(true) as HTMLElement);
        }
      });

    this.element
      .querySelectorAll<HTMLElement>("[data-autocomplete-v2-heading]")
      .forEach((heading) => {
        const group = heading.dataset.autocompleteV2Heading;
        if (group) {
          this.headingTemplates.set(
            group,
            heading.cloneNode(true) as HTMLElement,
          );
        }
      });
  }

  private parseItems(): void {
    try {
      const rawData = this.element.dataset.autocompleteItems || "[]";
      this.items = JSON.parse(rawData);
    } catch (e) {
      this.items = [];
    }
  }

  private setupAttributeObserver(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-autocomplete-items"
        ) {
          this.handleItemsUpdate();
        }
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-autocomplete-loading"
        ) {
          const isLoading =
            this.element.getAttribute("data-autocomplete-loading") === "true";
          if (isLoading) {
            this.renderLoading();
          } else {
            if (
              this.inputElement &&
              this.inputElement.value.trim().length >= this.minChars
            ) {
              this.renderSuggestions(this.items);
            }
          }
        }
      });
    });

    observer.observe(this.element, {
      attributes: true,
      attributeFilter: ["data-autocomplete-items", "data-autocomplete-loading"],
    });
  }

  private handleItemsUpdate(): void {
    this.parseItems();
    if (
      this.inputElement &&
      this.inputElement.value.trim().length >= this.minChars
    ) {
      const isLoading =
        this.element.getAttribute("data-autocomplete-loading") === "true";
      if (!isLoading) {
        this.renderSuggestions(this.items);
      }
    }
  }

  private setupUserInteractions(): void {
    if (!this.inputElement) return;
    this.suggestionContainer?.classList.add("search-container__hidden");

    this.inputElement.addEventListener("input", () => {
      this.handleInput();
      this.toggleClearIcon();
    });

    this.inputElement.addEventListener("blur", () => {
      this.toggleClearIcon();
    });

    document.addEventListener("modal:opened", () => {
      this.handleInput();
    });

    document.addEventListener("modal:closed", () => {
      this.hideList();
    });

    document.addEventListener("click", (e) => {
      const target = e.target as Node;
      if (!this.element.contains(target)) {
        this.hideList();
      }
    });
  }

  private setupExternalControl(): void {
    this.element.addEventListener("autocomplete:clear", () => this.clear());
    this.element.addEventListener("autocomplete:clearSelection", () =>
      this.clearSelection(),
    );
    this.element.addEventListener("autocomplete:enable", () => this.enable());
    this.element.addEventListener("autocomplete:disable", () => this.disable());

    this.element.addEventListener("autocomplete:set-item", (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        const shouldEmit = !!detail.emitEvent;
        this.selectItem(detail, shouldEmit);
      }
    });
  }

  private toggleClearIcon(): void {
    if (!this.clearIcon || !this.inputElement) return;

    if (this.inputElement.value.trim().length > 0) {
      this.clearIcon.classList.add("core-inputText__icon");
      this.clearIcon.classList.remove("core-inputText__icon--hidden");
    } else {
      this.clearIcon.classList.remove("core-inputText__icon");
      this.clearIcon.classList.add("core-inputText__icon--hidden");
    }
  }

  private handleInput(): void {
    if (this.isDisabled || !this.inputElement) {
      this.hideList();
      return;
    }

    const term = this.inputElement.value.trim().toLowerCase();

    if (term.length < this.minChars) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.hideList();
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.element.dispatchEvent(
        new CustomEvent("autocomplete:search", {
          detail: { query: term },
          bubbles: true,
        }),
      );

      if (this.element.hasAttribute("data-autocomplete-loading")) {
        this.element.setAttribute("data-autocomplete-loading", "true");
      } else if (this.items.length > 0) {
        const filtered = this.items.filter(
          (item) =>
            item.title.toLowerCase().includes(term) ||
            item.description.toLowerCase().includes(term) ||
            item.group?.toLowerCase().includes(term),
        );
        this.renderSuggestions(filtered);
      }
    }, this.searchTime);
  }

  private renderLoading(): void {
    if (!this.listContainer) return;

    this.listContainer.innerHTML = `
      <li class="core-suggestions__item core-suggestions__item--loading">
        <div
          class="core-spinner core-spinner--base core-spinner--round core-spinner--positive"
          role="status"
          aria-label="Cargando sugerencias"
          aria-live="polite"
        >
          <div class="core-spinner__track">
            <svg class="core-spinner__svg" viewBox="0 0 100 100" aria-hidden="true">
              <circle class="core-spinner__circle core-spinner__circle--base" cx="50" cy="50" r="45"></circle>
              <circle class="core-spinner__circle core-spinner__circle--segment" cx="50" cy="50" r="45"></circle>
            </svg>
          </div>
        </div>
      </li>
    `;
    this.showList();
  }

  private renderSuggestions(suggestions: SuggestionItemV2[]): void {
    if (!this.listContainer) return;
    this.listContainer.innerHTML = "";

    if (suggestions.length === 0) {
      this.hideList();
      return;
    }

    let currentGroup = "";

    suggestions.forEach((item) => {
      if (item.group && item.group !== currentGroup) {
        currentGroup = item.group;
        this.listContainer!.appendChild(this.getGroupHeading(item));
      }

      this.listContainer!.appendChild(this.getSuggestionItem(item));
    });
    this.showList();
  }

  private getGroupHeading(item: SuggestionItemV2): HTMLElement {
    if (item.group) {
      const template = this.headingTemplates.get(item.group);
      if (template) {
        return template.cloneNode(true) as HTMLElement;
      }
    }

    return this.createGroupHeading(item.group || "", item.groupIcon);
  }

  private getSuggestionItem(item: SuggestionItemV2): HTMLElement {
    const template = this.itemTemplates.get(String(item.key));
    const li = template
      ? (template.cloneNode(true) as HTMLElement)
      : this.createSuggestionItem(item);

    li.addEventListener("mousedown", (e) => {
      e.preventDefault();
      this.selectItem(item);
    });

    li.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.selectItem(item);
      }
    });

    return li;
  }

  private createSuggestionItem(item: SuggestionItemV2): HTMLLIElement {
    const li = document.createElement("li");
    li.className = [
      "core-suggestions__item",
      item.highlighted && "core-suggestions__item--highlight",
      item.wrap && "core-suggestions__item--wrap",
    ]
      .filter(Boolean)
      .join(" ");
    li.setAttribute("role", "option");
    li.tabIndex = 0;

    const content = document.createElement("div");
    content.className = "core-suggestions__content";

    const title = document.createElement("span");
    title.className = "core-suggestions__title";
    title.textContent = item.title;

    const description = document.createElement("span");
    description.className = "core-suggestions__description";
    description.textContent = item.description;

    if (item.group) {
      const row = document.createElement("div");
      row.className = "core-suggestions__row";
      row.append(title, description);
      content.appendChild(row);
    } else {
      content.append(title, description);
    }

    li.appendChild(content);

    return li;
  }

  private createGroupHeading(group: string, groupIcon?: string): HTMLLIElement {
    const li = document.createElement("li");
    li.className = "core-suggestions__heading";
    li.setAttribute("role", "presentation");

    if (groupIcon) {
      const icon = document.createElement("span");
      icon.className = `icon__svg--5 dsc-co-icon-${groupIcon}`;
      icon.setAttribute("aria-hidden", "true");
      li.appendChild(icon);
    }

    const label = document.createElement("span");
    label.className = "core-suggestions__heading-label";
    label.textContent = group;
    li.appendChild(label);

    return li;
  }

  private selectItem(item: SuggestionItemV2, emitEvent: boolean = true): void {
    this.oldSelection = { ...this.selectionItem } as SuggestionItemV2;
    if (this.inputElement) {
      this.inputElement.value = item.title;
      this.selectionItem = item;
      this.toggleClearIcon();
    }

    if (emitEvent) {
      this.element.dispatchEvent(
        new CustomEvent("suggestionSelected", {
          detail: item,
          bubbles: true,
        }),
      );
    }

    this.hideList();
  }

  private showList(): void {
    if (this.listContainer) {
      this.suggestionContainer?.classList.remove(this.hiddenClass);
      if (this.visibleClass) {
        this.suggestionContainer?.classList.add(this.visibleClass);
      }
    }
  }

  private hideList(): void {
    if (this.listContainer) {
      this.suggestionContainer?.classList.add(this.hiddenClass);
      if (this.visibleClass) {
        this.suggestionContainer?.classList.remove(this.visibleClass);
      }
    }
  }

  public clear(): void {
    if (this.inputElement) {
      this.inputElement.value = "";
      this.toggleClearIcon();
    }
    this.hideList();
  }

  public clearSelection(): void {
    this.clear();
    this.selectionItem = null;
    this.oldSelection = null;
  }

  public enable(): void {
    this.isDisabled = false;
    this.element.removeAttribute("data-autocomplete-disabled");
  }

  public disable(): void {
    this.isDisabled = true;
    this.element.setAttribute("data-autocomplete-disabled", "true");
    this.hideList();
  }
}

PluginRegistry.register("autocompleteV2", AutocompleteV2);
